import { compose, mapProps, withHandlers, withProps, branch } from "recompose";
import React from "react";
import TopicMap from "app/topics/TopicMap";
import TopicLayout from "app/topics/TopicLayout";
import ProposalSidebar from "app/proposals/ProposalSidebar";
import ProposalList from "app/proposals/ProposalList";
import ProposalAddForm from "app/proposals/ProposalAddForm";
import Sidebar from "app/layout/Sidebar";
import { withAuth } from "app/auth/hocs";
import { connectProposalState } from "app/proposals/hocs";
import { withTopics, fetchTopic } from "./hocs";
import { createHocBase } from "app/utils";
import TopicDetail from "./TopicDetail";
import { injectIntl } from "react-intl";

const listProposals = props => {
  const {
    query: { topicId, userId },
    topics,
    proposals
  } = props;
  let ids;
  if (!topicId) {
    ids = Object.keys(proposals.proposalsById);
  } else {
    ids = topics.topicsById[topicId].proposals;
  }
  let proposalList = ids.map(id => proposals.proposalsById[id]);
  if (userId) {
    proposalList = proposalList.filter(p => p.userId === userId);
  }
  proposalList.sort((a, b) => {
    return b.ui.sortableDate - a.ui.sortableDate;
  });
  return { proposalList };
};

const legendImageUrl = layer => {
  if (layer.baseURL && layer.options) {
    const imageUrl = `${
      layer.baseURL
    }service=WMS&version=1.1.1&request=GetLegendGraphic&layer=${
      layer.options.layers
    }&styles=${layer.options.styles}&format=${layer.options.format}`;
    return imageUrl;
  }
  return undefined;
};

const selectSidebar = compose(
  withHandlers({
    onSidebarClose: props => () => {
      const { history, topicId } = props;
      const path = `/topics/${topicId}`;
      const urlSearch = window.location.search;
      history.push(path + urlSearch);
    }
  }),
  branch(
    props => props.topicAction === "proposals",
    compose(
      injectIntl,
      withProps(props => {
        const { topicId, intl } = props;
        const sidebarTitle = intl.formatMessage({
          id: "sidebar.title.allproposals",
          defaultMessage: "All proposals"
        });
        return { query: { topicId }, sidebarTitle };
      }),
      withProps(listProposals)
    )
  ),
  branch(
    props => props.topicAction === "proposals/my",
    compose(
      withAuth,
      injectIntl,
      withProps(props => {
        const {
          topicId,
          auth: { userId },
          intl
        } = props;
        const sidebarTitle = intl.formatMessage({
          id: "topic.menu.myproposals",
          defaultMessage: "My proposals"
        });
        return { query: { topicId, userId }, sidebarTitle };
      }),
      withProps(listProposals)
    )
  ),
  injectIntl,
  mapProps(props => {
    const {
      topicAction,
      proposalAction,
      onSidebarClose,
      proposalList,
      sidebarTitle,
      proposal,
      isLegend,
      intl,
      ...rest
    } = props;
    const { topicId, proposalId, history, topic } = rest;
    let sidebar = null;

    if (proposalList) {
      sidebar = (
        <Sidebar title={sidebarTitle} onClose={onSidebarClose}>
          <ProposalList proposals={proposalList} />
        </Sidebar>
      );
    } else if (topicAction === "proposals/add") {
      sidebar = (
        <Sidebar
          title={intl.formatMessage({
            id: "topic.menu.addproposal",
            defaultMessage: "Add proposal"
          })}
          onClose={onSidebarClose}
        >
          <ProposalAddForm
            history={history}
            onCancelClick={onSidebarClose}
            topicId={topicId}
          />
        </Sidebar>
      );
    } else if (topicAction === "details") {
      sidebar = (
        <Sidebar
          title={intl.formatMessage({
            id: "sidebar.topic.details",
            defaultMessage: "Topic details"
          })}
          onClose={onSidebarClose}
        >
          <TopicDetail topic={topic} />
        </Sidebar>
      );
    } else if (proposalId) {
      sidebar = (
        <ProposalSidebar
          topicId={topicId}
          onClose={onSidebarClose}
          proposalId={proposalId}
          history={history}
          userId={proposal && proposal.userId}
        />
      );
    } else if (isLegend) {
      let layers;
      if (topic) {
        layers = topic.config.leaflet.layersControl.baseLayers.concat(
          topic.config.leaflet.layersControl.overlays
        );
      } else {
        layers = [];
      }

      const imageUrls = layers
        .map(layer => legendImageUrl(layer.layer))
        .filter(imageUrl => imageUrl !== undefined);

      sidebar = (
        <Sidebar title="Legend" onClose={onSidebarClose}>
          <div className="pt-card pt-elevation-1">
            {imageUrls.map(imageUrl => (
              <div key={imageUrl}>
                <img src={imageUrl} />
              </div>
            ))}
          </div>
        </Sidebar>
      );
    }

    return { ...rest, sidebar, proposalList };
  })
);

const withTopicMap = withProps(props => {
  const {
    topics,
    topic,
    topicId,
    proposalId,
    topicAction,
    proposalList
  } = props;
  return {
    topicMap: (
      <TopicMap
        topics={topics}
        topic={topic}
        topicId={topicId}
        proposalId={proposalId}
        topicAction={topicAction}
        proposalList={proposalList}
      />
    )
  };
});

const endsWith = (string, suffix) => {
  return string.indexOf(suffix, string.length - suffix.length) !== -1;
};

const flattenMatchParams = mapProps(props => {
  const {
    history,
    match: { params, url }
  } = props;
  return {
    history,
    ...params,
    isLegend: endsWith(url, "/legend")
  };
});

const saveLastVisited = Component =>
  class extends createHocBase(Component) {
    save(topicId, proposalId) {
      this.props.setLastVisited({ topicId, proposalId });
    }
    componentDidMount() {
      this.save(this.props.topicId, this.props.proposalId);
    }
    componentWillReceiveProps(nextProps) {
      if (
        this.props.topicId !== nextProps.topicId ||
        (nextProps.proposalId && this.props.proposalId !== nextProps.proposalId)
      ) {
        this.save(nextProps.topicId, nextProps.proposalId);
      }
    }
  };

const withConfigParams = withProps(props => ({
  maxTopicTitleLength: 20
}));

export default compose(
  flattenMatchParams,
  connectProposalState,
  withTopics,
  fetchTopic,
  branch(({ topic }) => Boolean(topic), selectSidebar),
  withTopicMap,
  withConfigParams,
  saveLastVisited
)(TopicLayout);



// WEBPACK FOOTER //
// ./src/app/topics/Topic.js